import { useState } from 'react';
import { Modal, Button, Input, UserSelect } from '../ui';
import { useKeepExport } from '../../hooks/useGoogleKeep';
import { useAuthStore } from '../../stores/authStore';

interface KeepExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  itemCount: number;
  onTokenExpired: () => void;
  defaultCollaborators: string[];
}

export function KeepExportModal({
  isOpen,
  onClose,
  itemCount,
  onTokenExpired,
  defaultCollaborators,
}: KeepExportModalProps) {
  const today = new Date().toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const [noteTitle, setNoteTitle] = useState(`Shopping List - ${today}`);
  const [collaborators, setCollaborators] = useState<string[]>([]);
  const exportKeep = useKeepExport();
  const currentUser = useAuthStore(s => s.user);

  // Sync collaborators when modal opens without useEffect
  const collaboratorsKey = isOpen ? defaultCollaborators.join(',') : '';
  const [prevCollaboratorsKey, setPrevCollaboratorsKey] = useState('');
  if (collaboratorsKey !== prevCollaboratorsKey) {
    setPrevCollaboratorsKey(collaboratorsKey);
    if (isOpen) {
      setCollaborators(defaultCollaborators);
    }
  }

  const handleExport = () => {
    exportKeep.mutate(
      { note_title: noteTitle, unchecked_only: true, collaborators },
      {
        onSuccess: () => {
          setTimeout(() => {
            handleClose();
          }, 1500);
        },
        onError: error => {
          const errorCode = (
            error as {
              response?: { data?: { errors?: Array<{ error_code: string }> } };
            }
          )?.response?.data?.errors?.[0]?.error_code;
          if (errorCode === 'KEEP_TOKEN_EXPIRED') {
            handleClose();
            onTokenExpired();
          }
        },
      },
    );
  };

  const handleClose = () => {
    exportKeep.reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Export to Google Keep"
      size="sm"
      footer={
        exportKeep.isSuccess ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={handleClose}>
              Close
            </Button>
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              gap: 'var(--spacing-sm)',
              justifyContent: 'flex-end',
            }}
          >
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleExport} disabled={!noteTitle || exportKeep.isPending}>
              {exportKeep.isPending ? 'Exporting...' : 'Export to Keep'}
            </Button>
          </div>
        )
      }
    >
      {exportKeep.isSuccess ? (
        <div className="keep-export-success">
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--color-success, #22c55e)"
            strokeWidth="2"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
          <p style={{ marginTop: 'var(--spacing-md)', fontWeight: 600 }}>Note created in Google Keep!</p>
          <p style={{ color: 'var(--color-text-light)', fontSize: '0.875rem' }}>
            "{exportKeep.data?.note_title}" with {exportKeep.data?.item_count} items
          </p>
        </div>
      ) : (
        <>
          <Input label="Note Title" value={noteTitle} onChange={e => setNoteTitle(e.target.value)} required />
          <div style={{ marginTop: 'var(--spacing-md)' }}>
            <UserSelect
              label="Share with"
              selectedEmails={collaborators}
              onChange={setCollaborators}
              excludeEmails={currentUser?.email ? [currentUser.email] : []}
            />
          </div>
          <p
            style={{
              marginTop: 'var(--spacing-md)',
              color: 'var(--color-text-light)',
              fontSize: '0.875rem',
            }}
          >
            {itemCount} item{itemCount !== 1 ? 's' : ''} will be exported
          </p>
        </>
      )}
    </Modal>
  );
}
